import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  fromEvent,
  map,
  merge,
  startWith,
  Subject,
  Subscription,
  takeUntil,
  distinctUntilChanged,
  combineLatest,
  BehaviorSubject,
  debounceTime,
  asapScheduler,
  filter,
  share,
  ReplaySubject,
  first,
  mergeMap,
  tap,
  shareReplay,
} from 'rxjs';
import { Rect } from '../../helpers/coords/rect';
import { RxUtils } from '../../utils/rx.utils';

export enum WidthType {
  SyncWithTarget = 'sync-with-target',
}

type VerticalAlignment = 'bottom' | 'top' | 'auto';
type HorizontalAlignment = 'left' | 'right' | 'center';

@Directive({
  selector: 'ng-template[dropdownContent]',
})
export class ContentTemplate {
  constructor(public templateRef: TemplateRef<any>) {}
}

@Component({
  selector: 'app-dropdown',
  templateUrl: 'dropdown.component.html',
  styleUrls: ['dropdown.component.scss'],
  host: {
    '[class.opened]': 'opened',
  },
})
export class DropdownComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ContentChild(ContentTemplate, { static: false })
  contentTpl: ContentTemplate;

  @ViewChild('divContent', { static: false })
  divContentElRef: ElementRef<HTMLDivElement>;

  @Input()
  target: HTMLElement;
  @Input()
  container: HTMLElement;

  private _opened = false;
  @Input()
  set opened(value) {
    value = !!value;
    if (this._opened !== value) {
      this._opened = value;
      this._openedSubjectInternal.next();
    }
  }

  get opened() {
    return this._opened;
  }

  @Output()
  openedChange = new EventEmitter<boolean>();

  @Output()
  onContentReady = new EventEmitter<void>();

  @Input()
  closeOutsideClickEnabled = true;
  @Input()
  animate = true;

  @Input()
  width: string = WidthType.SyncWithTarget;

  private _verticalAlignmentSubject = new BehaviorSubject<VerticalAlignment>(
    'auto'
  );

  @Input()
  get verticalAlignment() {
    return this._verticalAlignmentSubject.getValue();
  }

  set verticalAlignment(value) {
    this._verticalAlignmentSubject.next(value ?? 'auto');
  }

  @Input()
  horizontalAlignment: HorizontalAlignment = 'left';

  private targetResizeSubscription: Subscription;

  private _contentReadySubject = new ReplaySubject<void>(1);

  private _openedSubjectInternal = new Subject<void>();
  private _widthSubjectInternal = new BehaviorSubject<void>(null);

  private lifecycle = new Subject<void>();

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {}

  ngOnInit(): void {
    this._contentReadySubject
      .pipe(first(), takeUntil(this.lifecycle))
      .subscribe(() => {
        const divContentEl = this.divContentElRef.nativeElement;

        bindEventsToEl(divContentEl)
          .pipe(takeUntil(this.lifecycle))
          .subscribe((e) => {
            e.stopPropagation();
          });

        bindEventsToEl(document.body)
          .pipe(takeUntil(this.lifecycle))
          .subscribe((e) => {
            if (
              !this.opened ||
              !this.closeOutsideClickEnabled ||
              this.target?.contains(e.target as any) ||
              divContentEl.contains(e.target as any)
            ) {
              return;
            }

            this.opened = false;
            this.openedChange.emit(this.opened);
          });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.target || changes.container) {
      this.handleTarget();
    } else if (changes.width) {
      this._widthSubjectInternal.next();
    }
  }

  ngAfterViewInit(): void {
    if (this.opened) {
      this._openedSubjectInternal.next();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onContentReadyInternal() {
    this._contentReadySubject.next();

    this.onContentReady.emit();
  }

  private handleTarget() {
    this.targetResizeSubscription?.unsubscribe();

    if (!this.target) {
      return;
    }

    this.targetResizeSubscription = this._contentReadySubject
      .pipe(
        first(),
        mergeMap(() => {
          const defaultObservable = merge(
            merge(
              this._openedSubjectInternal.pipe(map(() => this.opened)),
              this.openedChange
            ).pipe(
              distinctUntilChanged(),
              filter((a) => a)
            ),
            fromEvent(window, 'resize')
          ).pipe(share());

          const container = this.container ?? this._document.body;
          const containerRectObservable = merge(
            RxUtils.observeElementResize(container).pipe(
              map(([entry]) => {
                const contentRect = entry.contentRect;
                const borderBoxSize = entry.borderBoxSize[0];

                return new Rect(
                  contentRect.left,
                  contentRect.top,
                  borderBoxSize.inlineSize,
                  borderBoxSize.blockSize
                );
              })
            ),
            defaultObservable.pipe(map(() => Rect.fromElement(container)))
          ).pipe(
            startWith(Rect.fromElement(container)),
            distinctUntilChanged((prev, curr) => {
              return prev.equals(curr);
            })
          );

          const targetRectObservable = merge(
            RxUtils.observeElementResize(this.target),
            defaultObservable
          ).pipe(
            map(() => Rect.fromElement(this.target)),
            startWith(Rect.fromElement(this.target)),
            distinctUntilChanged((prev, curr) => {
              return prev.equals(curr);
            })
          );

          const divContentEl = this.divContentElRef.nativeElement;
          const divContentRectObservable = RxUtils.observeElementResize(
            divContentEl
          ).pipe(
            map(([entry]) => {
              const contentRect = entry.contentRect;
              const borderBoxSize = entry.borderBoxSize[0];

              return new Rect(
                contentRect.left,
                contentRect.top,
                borderBoxSize.inlineSize,
                borderBoxSize.blockSize
              );
            }),
            startWith(Rect.fromElement(divContentEl)),
            distinctUntilChanged((prev, curr) => {
              return prev.width === curr.width && prev.height === curr.height;
            }),
            tap((rect) => {
              divContentEl.style.setProperty(
                '--dropdown-real-width',
                rect.width + 'px'
              );
              divContentEl.style.setProperty(
                '--dropdown-real-height',
                rect.height + 'px'
              );
            })
          );

          return combineLatest(
            containerRectObservable,
            targetRectObservable,
            divContentRectObservable,
            this._verticalAlignmentSubject.pipe(distinctUntilChanged()),
            this._widthSubjectInternal
          ).pipe(
            debounceTime(0, asapScheduler),
            tap(([containerRect, targetRect, rect]) => {
              let isBottomMode: boolean;
              if (this.verticalAlignment === 'auto') {
                rect = rect.clone();
                rect.y = targetRect.y + targetRect.height;

                if (rect.isInsideOfVertically(containerRect)) {
                  isBottomMode = true;
                } else {
                  isBottomMode = false;
                }
              } else {
                switch (this.verticalAlignment) {
                  case 'top':
                    isBottomMode = false;
                    break;
                  default:
                    isBottomMode = true;
                    break;
                }
              }

              let posY: number;
              if (isBottomMode) {
                posY = targetRect.bottom;
              } else {
                posY = targetRect.y - rect.height;
              }

              this.renderer.setStyle(divContentEl, 'top', `${posY}px`);
            }),
            tap(([containerRect, targetRect]) => {
              this.handleTargetRect(targetRect, containerRect);
            })
          );
        }),

        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  private handleTargetRect(targetRect: Rect, containerRect: Rect) {
    targetRect = targetRect.clone();
    targetRect.relativeTo(containerRect.pos());

    const element = this.divContentElRef.nativeElement;

    element.style.setProperty(
      '--dropdown-target-pos-x',
      `${targetRect?.x ?? 0}px`
    );
    element.style.setProperty(
      '--dropdown-target-pos-y',
      `${targetRect?.y ?? 0}px`
    );
    element.style.setProperty(
      '--dropdown-target-width',
      `${targetRect?.width ?? 0}px`
    );
    element.style.setProperty(
      '--dropdown-target-height',
      `${targetRect?.height ?? 0}px`
    );

    element.style.setProperty(
      '--dropdown-container-width',
      containerRect.width + 'px'
    );
    element.style.setProperty(
      '--dropdown-container-height',
      containerRect.height + 'px'
    );

    if (this.width === WidthType.SyncWithTarget) {
      this.renderer.setStyle(element, 'width', `${targetRect?.width ?? 0}px`);
    } else {
      this.renderer.setStyle(element, 'width', this.width ?? 'auto');
    }
  }
}

function bindEventsToEl(element: HTMLElement) {
  return merge(
    fromEvent(element, 'click', {
      passive: true,
    }),
    fromEvent(element, 'mousedown', {
      passive: true,
    }),
    fromEvent(element, 'wheel', {
      passive: true,
    }),
    fromEvent(element, 'touchstart', {
      passive: true,
    }),
    fromEvent(element, 'touchmove', {
      passive: true,
    })
  );
}
